import type { CompanyProfile, EntityOrder, EntityStats, EntityType, PaginatedResponse } from '@/shared/types/company.types';

const MOCK_PROFILES: Record<EntityType, Omit<CompanyProfile, 'id' | 'owner_id'>> = {
  restaurant: {
    entity_type: 'restaurant',
    organization_name: 'Restaurant Le Palmier',
    institution_type: 'Restauration',
    goods_types: 'Repas chauds,Boissons,Desserts',
    responsible_name: 'Moussa Keita',
    responsible_contact: '+224620111222',
    monthly_volume: '500-1000',
    website: 'https://lepalmier.gn',
    address: 'Kaloum, Conakry',
    landmark: 'Face à la gare',
    specific_instructions: 'Entrée par la cour arrière',
  },
  commercant: {
    entity_type: 'commercant',
    organization_name: 'Boutique Fatou',
    goods_types: 'Textile,Accessoires',
    responsible_name: 'Fatou Bah',
    responsible_contact: '+224620333444',
    monthly_volume: '100-300',
    address: 'Matam, Conakry',
    landmark: 'Marché central',
  },
  artisan: {
    entity_type: 'artisan',
    organization_name: 'Atelier Mamadou',
    goods_types: 'Mobilier,Sculpture',
    responsible_name: 'Mamadou Sylla',
    monthly_volume: '50-100',
    address: 'Ratoma, Conakry',
  },
  ecommerce: {
    entity_type: 'ecommerce',
    organization_name: 'GuinéeShop Online',
    goods_types: 'Électronique,Mode,Maison',
    responsible_name: 'Ibrahima Diallo',
    monthly_volume: '1000+',
    website: 'https://guineeshop.com',
    address: 'Dixinn, Conakry',
  },
  pme: {
    entity_type: 'pme',
    organization_name: 'SOGUIPAH',
    institution_type: 'Industrie',
    goods_types: 'Produits manufacturés',
    responsible_name: 'Directeur Logistique',
    monthly_volume: '500+',
    address: 'Zone industrielle, Conakry',
  },
  'grande-entreprise': {
    entity_type: 'grande-entreprise',
    organization_name: 'Orange Guinée',
    institution_type: 'Télécommunications',
    monthly_volume: '5000+',
    address: 'Kaloum, Conakry',
  },
  demenagement: {
    entity_type: 'demenagement',
    organization_name: 'Déménageurs Express GN',
    goods_types: 'Mobilier,Électroménager',
    responsible_name: 'Ousmane Barry',
    monthly_volume: '200-400',
    address: 'Conakry',
  },
  'partenaire-logistique': {
    entity_type: 'partenaire-logistique',
    organization_name: 'TransAfrique Logistics',
    monthly_volume: '2000+',
    address: 'Port de Conakry',
  },
  'partenaire-financier': {
    entity_type: 'partenaire-financier',
    organization_name: 'Orange Money Guinée',
    institution_type: 'Mobile Money',
    monthly_volume: '10000+',
    address: 'Kaloum',
  },
  'point-relais': {
    entity_type: 'point-relais',
    organization_name: 'Relais Matam Centre',
    goods_types: 'Colis général',
    responsible_name: 'Aissatou Camara',
    responsible_contact: '+224620555666',
    address: 'Matam, près du marché',
    landmark: 'Boutique bleue',
  },
  administration: {
    entity_type: 'administration',
    organization_name: 'Ministère des Transports',
    institution_type: 'Administration publique',
    address: 'Kaloum, Conakry',
  },
  'organisation-internationale': {
    entity_type: 'organisation-internationale',
    organization_name: 'UNICEF Guinée',
    institution_type: 'Organisation internationale',
    address: 'Dixinn, Conakry',
  },
  ong: {
    entity_type: 'ong',
    organization_name: 'Action Contre la Faim',
    institution_type: 'ONG humanitaire',
    goods_types: 'Aide alimentaire,Médicaments',
    address: 'Conakry',
  },
  'health-professional': {
    entity_type: 'health-professional',
    organization_name: 'Pharmacie du Peuple',
    institution_type: 'Pharmacie',
    goods_types: 'Médicaments,Produits de santé',
    address: 'Ratoma, Conakry',
  },
  'educational-institution': {
    entity_type: 'educational-institution',
    organization_name: 'École Sainte-Marie',
    institution_type: 'École primaire',
    goods_types: 'Documents,Fournitures,Repas',
    address: 'Marcory, Conakry',
  },
  'event-organizer': {
    entity_type: 'event-organizer',
    organization_name: 'Events GN Pro',
    goods_types: 'Matériel événementiel,Décoration',
    responsible_name: 'Kadiatou Sow',
    address: 'Conakry',
  },
};

export function getMockProfile(ownerId: number, entityType: EntityType): CompanyProfile {
  const base = MOCK_PROFILES[entityType];
  return { id: 1, owner_id: ownerId, ...base };
}

const ALL_ORDERS: EntityOrder[] = Array.from({ length: 42 }, (_, i) => ({
  id: i + 1,
  code_suivi: `ONA-2026-${String(1000 + i).padStart(4, '0')}`,
  statut: ['en_attente', 'en_transit', 'livré', 'annulé'][i % 4],
  adresse_ramassage: ['Kaloum', 'Matam', 'Ratoma', 'Dixinn'][i % 4] + ', Conakry',
  adresse_livraison: ['Cosa', 'Sonfonia', 'Kipé', 'Lambanyi'][i % 4] + ', Conakry',
  montant: 15000 + (i * 3500) % 200000,
  devise: 'GNF',
  type_livraison: ['Express', 'Groupée', 'Spécialisée', 'Interurbaine'][i % 4],
  client_nom: ['Aminata D.', 'Mamadou C.', 'Fatou B.', 'Ibrahima K.'][i % 4],
  livreur_nom: i % 4 !== 0 ? ['Kouassi J.', 'Aya M.', 'Traoré O.'][i % 3] : undefined,
  created_at: new Date(Date.now() - i * 86400000).toISOString(),
}));

export function getMockOrders(
  page = 1,
  perPage = 10,
  search = '',
): PaginatedResponse<EntityOrder> {
  let filtered = ALL_ORDERS;
  if (search) {
    const q = search.toLowerCase();
    filtered = ALL_ORDERS.filter(
      (o) =>
        o.code_suivi.toLowerCase().includes(q) ||
        o.client_nom.toLowerCase().includes(q) ||
        o.adresse_livraison.toLowerCase().includes(q),
    );
  }
  const total = filtered.length;
  const last_page = Math.max(1, Math.ceil(total / perPage));
  const current_page = Math.min(page, last_page);
  const start = (current_page - 1) * perPage;
  const data = filtered.slice(start, start + perPage);

  return {
    data,
    meta: {
      current_page,
      last_page,
      per_page: perPage,
      total,
      from: total ? start + 1 : 0,
      to: Math.min(start + perPage, total),
    },
  };
}

export function getMockStats(): EntityStats {
  return {
    commandes_en_cours: 8,
    commandes_terminees: 156,
    revenus_mois: 4850000,
    satisfaction: 4.6,
  };
}

export const MOCK_CLIENTS = [
  { id: 1, name: 'Bureau SOGB', address: 'Plateau', frequence: '5x/sem', type: 'Entreprise' },
  { id: 2, name: 'Famille Diabaté', address: 'Cocody', frequence: '2x/sem', type: 'Particulier' },
  { id: 3, name: 'École Sainte-Marie', address: 'Marcory', frequence: 'Quotidien', type: 'Institution' },
];

export const MOCK_TRANSACTIONS = [
  { id: 1, ref: 'TX-001', montant: 450000, gateway: 'Orange Money', statut: 'verse', date: '2026-06-01' },
  { id: 2, ref: 'TX-002', montant: 120000, gateway: 'MTN MoMo', statut: 'en_attente', date: '2026-06-05' },
  { id: 3, ref: 'TX-003', montant: 85000, gateway: 'Cash', statut: 'verse', date: '2026-06-07' },
];

export const MOCK_STOCK = [
  { id: 1, code: 'COL-8821', client: 'Aminata D.', statut: 'en_stock', arrive: '2026-06-07' },
  { id: 2, code: 'COL-8822', client: 'Mamadou C.', statut: 'a_retirer', arrive: '2026-06-06' },
];
